"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, AnimatePresence } from "motion/react";
import { MapPin, Clock, Tag, ChevronRight, Compass } from "lucide-react";
import { ICMS } from "@/store/homeSlice";
import { createLookupObject, cleanHeaderTags } from "@/_helper/helper";
import { useAppSelector } from "@/_hooks/hooks";

const categories = [
  "All",
  "Wildlife",
  "Adventure",
  "Nature",
  "Culture",
  "One-day trips",
  "Gardens",
];

interface AttractionsProps {
  cmsData: ICMS[];
  attractionsData: any[];
}

export default function AttractionsClient({
  cmsData,
  attractionsData,
}: AttractionsProps) {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, ICMS>>({});
  const { loading } = useAppSelector((state) => state.hotel);
  const BannerList = useAppSelector((state) => state.home.BannerList);

  useEffect(() => {
    if (cmsData && cmsData.length > 0) {
      setLookUpCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);
  const banner: any =
    BannerList?.find((banner: any) => banner?.BannerType === "attractions") ||
    {};

  const filteredAttractions =
    selectedCategory === "All"
      ? attractionsData
      : attractionsData?.filter(
          (a: any) => a.AttractionCategory === selectedCategory,
        ) || [];

  const featuredAttractions =
    attractionsData?.filter((a: any) => a.IsMustVisit) || [];

  return (
    <>
      <main className="min-h-screen bg-[#FCFBF9] text-gray-900 font-sans">
        {/* Hero Section */}
        <section className="relative h-[50vh] overflow-hidden">
          {banner?.BannerImage && (
            <Image
              src={banner?.BannerImage || ""}
              alt={banner?.BannerTitle || "Banner Image"}
              fill
              className="object-cover"
              priority
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 via-gray-900/0 to-gray-900/0 pointer-events-none"></div>

          <div className="absolute inset-0 flex items-end pointer-events-none">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                {banner?.SubTitle && (
                  <div className="inline-flex items-center gap-4 px-5 py-2 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full mb-8">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                      <span className="w-2 h-2 rounded-full bg-white/80" />
                      <span className="w-2 h-2 rounded-full bg-green-600 animate-pulse" />
                    </div>
                    <p className="text-white text-xs font-bold tracking-[0.4em] uppercase">
                      {banner?.SubTitle}
                    </p>
                  </div>
                )}
                <h1 className="text-5xl md:text-7xl font-bold text-amber-300 mb-6 drop-shadow-2xl">
                  {banner?.BannerTitle || "Our Attractions"}
                </h1>
                {banner?.Description && (
                  <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto drop-shadow-lg mb-6">
                    {banner?.Description}
                  </p>
                )}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Category Filters */}
        <div className="sticky top-20 z-40 bg-white/80 backdrop-blur-2xl border-b border-gray-100 shadow-sm">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex overflow-x-auto gap-4 py-6 no-scrollbar items-center">
              <div className="p-2 bg-blue-50 rounded-lg text-blue-600 mr-2 flex-shrink-0">
                <Compass size={20} />
              </div>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-8 py-2.5 rounded-full font-bold whitespace-nowrap transition-all text-sm tracking-wide border-2 ${
                    selectedCategory === category
                      ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-xl scale-105 border-orange-400"
                      : "bg-white text-gray-600 border-gray-100 hover:border-orange-200 hover:bg-orange-50/30"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Featured Attractions - Must Visit */}
        {featuredAttractions?.length > 0 && (
          <section className="py-24 px-6 lg:px-8 bg-gradient-to-b from-[#FCFBF9] to-white">
            <div className="max-w-7xl mx-auto">
              {lookUpCMSData?.["Attractions - Section 1: Must-Visit"] && (
                <div
                  className="flex flex-col items-center text-center mb-16 blog-content"
                  dangerouslySetInnerHTML={{
                    __html:
                      cleanHeaderTags(lookUpCMSData?.["Attractions - Section 1: Must-Visit"]
                        ?.Template),
                  }}
                />
              )}
              {/* <div className="flex flex-col items-center text-center mb-16">
                <div className="h-[2px] w-12 bg-orange-500 rounded-full mb-4"></div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 font-playfair mb-4">
                  Must-Visit{" "}
                  <span className="text-orange-600">Experiences</span>
                </h2>
                <p className="text-gray-500 font-light max-w-xl">
                  Curated list of absolute masterpieces that you simply cannot
                  miss during your visit.
                </p>
              </div> */}

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
                {featuredAttractions.map((attraction: any, idx: number) => (
                  <motion.div
                    key={attraction.ID}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                  >
                    <Link
                      href={`/attractions/${attraction.AttractionURL}/`}
                      className="group relative block aspect-[4/5] overflow-hidden rounded-[2.5rem] shadow-2xl hover:shadow-orange-500/20 transition-all duration-700"
                    >
                      <Image
                        src={attraction.MainImage || "/placeholder.jpg"}
                        alt={attraction.Title || "Attraction"}
                        fill
                        className="object-cover transition-transform duration-1000 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent p-10 flex flex-col justify-end">
                        <div className="flex items-center gap-2 mb-4">
                          <span className="px-3 py-1 bg-orange-600 text-white text-[10px] font-bold uppercase tracking-widest rounded-full">
                            MUST VISIT
                          </span>
                          <span className="px-3 py-1 bg-white/10 backdrop-blur-md text-white text-[10px] font-bold uppercase tracking-widest rounded-full border border-white/20">
                            {attraction.AttractionCategory}
                          </span>
                        </div>
                        <h3 className="text-3xl font-bold text-white mb-4 font-playfair tracking-tight group-hover:text-amber-400 transition-colors">
                          {attraction.Title}
                        </h3>
                        <div className="flex items-center gap-6 text-white/80 text-xs font-bold tracking-widest uppercase mb-6">
                          <span className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-orange-500" />
                            {attraction.Distance}
                          </span>
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-green-500" />
                            {attraction.TravelTime}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-white font-bold text-sm group-hover:gap-4 transition-all">
                          Explore Venue{" "}
                          <ChevronRight size={18} className="text-orange-500" />
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Full Grid Section */}
        <section className="py-24 px-6 lg:px-8 border-t border-gray-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
              <div className="border-l-4 border-blue-600 pl-6">
                <p className="text-blue-600 font-bold uppercase text-xs tracking-widest mb-1">
                  DISCOVER MORE
                </p>
                <h3 className="text-3xl md:text-5xl font-bold text-gray-900 font-playfair">
                  {selectedCategory === "All"
                    ? "All Global Wonders"
                    : `${selectedCategory} Collection`}
                </h3>
              </div>
              <div className="text-gray-400 font-light italic">
                Showing {filteredAttractions.length} unique captures
              </div>
            </div>

            <div className="relative">
              {filteredAttractions.length > 0 ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-8">
                  <AnimatePresence mode="popLayout">
                    {filteredAttractions.map((attraction: any, idx: number) => (
                      <motion.div
                        key={attraction.ID}
                        layout
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.4 }}
                      >
                        <Link
                          href={`/attractions/${attraction.AttractionURL}/`}
                          className="group bg-white rounded-[2rem] overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 flex flex-col h-full border border-gray-50"
                        >
                          <div className="aspect-video relative overflow-hidden">
                            <Image
                              src={attraction.MainImage || "/placeholder.jpg"}
                              alt={attraction.Title || "Attraction"}
                              fill
                              className="object-cover transition-transform duration-700 group-hover:scale-110"
                            />
                            <div className="absolute top-4 right-4 animate-fade-in opacity-0 group-hover:opacity-100">
                              <div className="bg-white/20 backdrop-blur-md p-3 rounded-full border border-white/30 text-white">
                                <ChevronRight size={20} />
                              </div>
                            </div>
                          </div>
                          <div className="p-8 flex-1 flex flex-col">
                            <div className="flex items-center gap-2 mb-4">
                              <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5">
                                <Tag className="w-3 h-3" />
                                {attraction.AttractionCategory}
                              </span>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-3 font-playfair">
                              {attraction.Title}
                            </h3>
                            <p className="text-gray-500 font-light text-sm line-clamp-3 mb-6 flex-1 leading-relaxed">
                              {attraction.TagLine}
                            </p>
                            <div className="pt-6 border-t border-gray-100 flex items-center justify-between text-[11px] font-bold tracking-tighter uppercase text-gray-400">
                              <span className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-orange-500" />
                                {attraction.Distance}
                              </span>
                              <span className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-green-600" />
                                {attraction.TravelTime}
                              </span>
                            </div>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="py-20 flex flex-col items-center justify-center text-center">
                  <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 mb-6">
                    <Compass size={40} className="animate-spin-slow" />
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">
                    No Attractions Found
                  </h4>
                  <p className="text-gray-500 max-w-md">
                    We couldn't find any attractions in the {selectedCategory}{" "}
                    category. Please try another one.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <style jsx global>{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </>
  );
}
